import { FilePenLine, MoreVertical, Trash2, RefreshCw } from '@/components/icons';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';
import { usePassengerDelete, usePassengerGetById, usePassengerRestore } from '../lib/hooks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { PaxDrawer } from './PaxDrawer';

interface PaxActionsSelectProps {
  passengerId: string;
  onPassengerDeleted: VoidFunction;
}

export function PaxActionsSelect({
  passengerId,
  onPassengerDeleted,
}: PaxActionsSelectProps) {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);

  const { mutate: deletePassenger } = usePassengerDelete();
  const { mutate: restorePassenger } = usePassengerRestore();
  const { data: passenger } = usePassengerGetById(passengerId);

  const fullName =
    `${passenger?.firstName || ''} ${passenger?.lastName || ''}`.trim() || 'Unknown';
  const isDeleted = passenger?.isDeleted === true;

  const handleEdit = () => {
    setIsEditDrawerOpen(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleRestore = () => {
    restorePassenger(
      { id: passengerId },
      {
        onSuccess: () => {
          toast.success('Passenger activated successfully');
        },
        onError: () => {
          toast.error('Failed to activate passenger');
        },
      }
    );
  };

  const handleDeletePassenger = () => {
    deletePassenger(
      { id: passengerId },
      {
        onSuccess: () => {
          toast.success('Passenger deleted successfully');
          setShowDeleteDialog(false);
          onPassengerDeleted();
        },
        onError: (error: Error & { code?: string }) => {
          console.error('Error deleting passenger:', error);
          toast.error('Failed to delete passenger');
        },
      }
    );
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger className="size-8 p-0 bg-content-primary-inverse hover:bg-content-primary-inverse/30 border-0 rounded-full flex items-center justify-center outline-none">
          <MoreVertical className="size-4 text-content-primary-bold" />
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {isDeleted ? (
            <DropdownMenuItem onClick={handleRestore}>
              <RefreshCw className="size-4" />
              Activate Passenger
            </DropdownMenuItem>
          ) : (
            <>
              <DropdownMenuItem onClick={handleEdit}>
                <FilePenLine className="size-4" />
                Edit Passenger
              </DropdownMenuItem>
              <DropdownMenuItem variant="destructive" onClick={handleDeleteClick}>
                <Trash2 className="size-4" />
                Delete Passenger
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-surface-L0">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Passenger</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {fullName}? You can activate the passenger back!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePassenger}
              className="bg-content-negative-bold hover:bg-content-negative-bold/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Passenger Drawer */}
      <PaxDrawer
        isOpen={isEditDrawerOpen}
        onClose={() => setIsEditDrawerOpen(false)}
        passengerId={passengerId}
      />
    </>
  );
}
